import {
  TextField,
  InputLabel,
  IconButton,
  makeStyles,
  Theme,
} from "@material-ui/core";
import AddCircleOutline from "@material-ui/icons/AddCircleOutline";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useCurrentUser, useEntityRef } from "@internal/plugin-utilities";
import { ReviewManualDetailsDBResponse } from "@internal/plugin-ops-review-common";
import { Entity } from "@backstage/catalog-model";

const useStyles = makeStyles((_theme: Theme) => ({
  label: {
    marginBottom: "10px",
  },
}));

interface Props {
  data: ReviewManualDetailsDBResponse;
  setUpstreamData: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
        user: string;
      }[]
    >
  >;
}

export const IssuesAndRecommendationsSection: React.FC<Props> = ({
  data,
  setUpstreamData,
}): JSX.Element => {
  const classes = useStyles();
  const [issuesAndRecommendations, setIssuesAndRecommendations] = useState<
    {
      value: string;
      user: string;
    }[]
  >([]);

  useEffect(() => {
    if (data) {
      setIssuesAndRecommendations(data.issuesAndRecommendations);
      setUpstreamData(data.issuesAndRecommendations);
    }
  }, [data, setIssuesAndRecommendations, setUpstreamData]);

  const currentUser = useCurrentUser();
  const currentUserEntityRef = useEntityRef({
    entity: (currentUser as Entity) || {
      kind: "User",
      metadata: { namespace: "default", name: "unknown" },
    },
  });

  const handleIssuesAndRecommendationsInputChange = useCallback(
    (event: ChangeEvent<{ value: unknown }>, id: number) => {
      const newIar = issuesAndRecommendations.slice();
      newIar[id] = {
        value: event.target.value as string,
        user: currentUserEntityRef,
      };
      setIssuesAndRecommendations(newIar);
      setUpstreamData(newIar);
    },
    [
      issuesAndRecommendations,
      setIssuesAndRecommendations,
      setUpstreamData,
      currentUserEntityRef,
    ]
  );

  const [issuesAndRecommendationsJsx, setIssuesAndRecommendationsJsx] =
    useState<JSX.Element[]>([
      <TextField
        id="iar-1"
        fullWidth
        onChange={(event: ChangeEvent<{ value: unknown }>) => {
          return handleIssuesAndRecommendationsInputChange(event, 1);
        }}
        key="iar-1"
      />,
    ]);

  // when the issues and recommendations changes update the jsx
  useEffect(() => {
    // setIssuesAndRecommendationsJsx(
    const jsx = issuesAndRecommendations.map(
      (_i: { value: string; user: string }, idx: number) => {
        return (
          <TextField
            id={`iar-${idx}`}
            fullWidth
            onChange={(e: ChangeEvent<{ value: unknown }>) =>
              handleIssuesAndRecommendationsInputChange(e, idx)
            }
            value={issuesAndRecommendations[idx].value}
            key={`iar-${idx}`}
          />
        );
      }
    );
    setIssuesAndRecommendationsJsx(jsx);
  }, [
    issuesAndRecommendations,
    handleIssuesAndRecommendationsInputChange,
    setIssuesAndRecommendations,
  ]);

  // Add an Issue and Recommendation
  const handleAddIssuesAndRecommendationsClick = () => {
    const jsx = issuesAndRecommendationsJsx.slice();
    const id = issuesAndRecommendationsJsx.length;

    jsx.push(
      <TextField
        id={`iar-${id}`}
        fullWidth
        onChange={(event: ChangeEvent<{ value: unknown }>) =>
          handleIssuesAndRecommendationsInputChange(event, id)
        }
        key={`iar-${id}`}
      />
    );
    setIssuesAndRecommendationsJsx(jsx);
  };

  return (
    <>
      <InputLabel className={classes.label}>
        Add Issues and Recommendations for the week{" "}
        <IconButton onClick={handleAddIssuesAndRecommendationsClick}>
          <AddCircleOutline />
        </IconButton>
      </InputLabel>
      {issuesAndRecommendationsJsx}
    </>
  );
};
